import { BedDouble, Calendar, ChatBubbleLeftRightSolid, MapPin, NavArrowDown, NavArrowLeft, NavArrowRight, User, WarningTriangle } from '@byway/icons';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import clsx from 'clsx';
import { isWithinInterval } from 'date-fns';
import { Field, type FormikErrors } from 'formik';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { Focusable } from 'react-aria-components';
import { Button, Text } from 'ui';
import { hasFieldErrors, parseDate } from 'utils';
import { formatDashedYYYYMMDD, formatSlashedDDMMYYYY } from 'utils/DateFormats';
import { BookButton } from '~/components/BookButton';
import { ContactUsButton } from '~/components/Journey/ContactUsButton';
import { Dates, Details, JourneyEditForm, TemplateEditForm } from '~/components/Journey/JourneyEditForm';
import { Stack } from '~/components/Layout/Stack';
import { Tooltip, TooltipTrigger } from '~/components/Tooltip';
import { TRPC_SETTINGS } from '~/constants/trpc';
import useJourneyData from '~/hooks/useJourneyData';
import { useJourneyEditErrorMessage } from '~/hooks/useJourneyEditErrorMessage';
import type { Locale } from '~/i18n/i18n-config';
import { getPathname } from '~/i18n/navigation';
import { api } from '~/libs/api';
import { useIsCheckingAvailability, useJourneyActions, useJourneyEditErrors } from '~/stores/journeyStore';
import { trackEvent } from '~/utils';
import { getFirstLegOriginId, isUSBlueprintTrip } from '~/utils/blueprintHelpers/isUSTrip';
import { ensureFutureDate, isJourneyExpired } from '~/utils/dateHelpers/dateHelpers';
import { isOriginInRegion } from '~/utils/originHelpers';
import { paxAges } from '~/utils/paxHelpers';
import { Notification } from '../Notification';

/** Top bar with date, origin, pax controls and availability check */
export default function JourneyTopBar({
  isTemplateTrip = false,
  isEditable
}: {
  isTemplateTrip?: boolean;
  isEditable?: boolean;
}) {
  const {
    actions,
    details,
    futurePricingTiers,
    region,
    journeyType,
    source,
    user,
    items,
    isStartDateInPast,
    isLastMinute,
    hasSurpriseMeAccommodation,
    expirationDateTime,
    uniqueId,
    bookingInformation
  } = useJourneyData();
  const t = useTranslations();
  const locale = useLocale() as Locale;
  const editErrorMessage = useJourneyEditErrorMessage();
  const isCheckingAvailability = useIsCheckingAvailability();
  const journeyEditErrors = useJourneyEditErrors();
  const {
    setIsCheckingAvailability,
    setJourneyEditErrors
  } = useJourneyActions();
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [liveRegionMessage, setLiveRegionMessage] = useState<string | null>(null);
  type FormValues = {
    startDate: string | null;
    closest_city: string;
    child_count: number;
    adult_count: number;
    room_count: number;
  };
  const {
    adult_count,
    childAges
  } = user;
  const closest_city = (() => {
    if (items[0]?.type === 'Journey') {
      return items[0].origin;
    }
    const firstJourney = items.find(item => item.type === 'Journey');
    if (firstJourney?.origin) {
      return firstJourney.origin;
    }
    return items[0] && 'title' in items[0] ? items[0].title : 'Unknown';
  })();
  // Check if a Journey created from Blueprint Template, or is the Blueprint Template page, or is a Playground Trip

  const isBlueprintJourney = source.creationSource === 'FROM_TEMPLATE' || source.creationSource === 'FROM_VIRTUAL_BLUEPRINT_TEMPLATE' || source.creationSource === 'FROM_VIRTUAL_BLUEPRINT';
  const isBlueprint = isTemplateTrip || isBlueprintJourney;
  const isPlaygroundTrip = source.creationSource.includes('PLAYGROUND');

  // Use default start date if current date is in the past
  const effectiveStartDate = formatDashedYYYYMMDD(ensureFutureDate(details.startDate));
  const isEarliestCheapestDate = effectiveStartDate === futurePricingTiers?.cheapestAvailableDate;
  const isFastOneWay = user.fastJourney === 'yes';
  // fallback for fast one way trips where region data is null
  const minDurationDays = region.minDurationDays ?? details.tripInDays;
  const maxDurationDays = region.maxDurationDays ?? details.tripInDays;
  const isEnquireOnly = typeof process !== 'undefined' && process.env.NEXT_PUBLIC_FORCE_ENQUIRY === 'true';
  const isExpired = isJourneyExpired(expirationDateTime, isBlueprint);
  const bookButtonCopy = t('journey.continue');
  const isBookDisabled = !!(journeyEditErrors.length || bookingInformation?.bookingStatus === 'BOOKED' || bookingInformation?.bookingStatus === 'OUTSTANDING' || hasSurpriseMeAccommodation && isLastMinute || isExpired || isCheckingAvailability);

  //disable editing if forcing enquiries only
  if (isEnquireOnly) {
    isEditable = false;
  }
  const formComponents = {
    Journey: JourneyEditForm,
    Template: TemplateEditForm
  };
  const EditForm = formComponents[journeyType === 'asTemplate' || isBlueprint || isPlaygroundTrip ? 'Template' : 'Journey'];
  const inputStyle = 'bg-white border rounded border-neutral-300 hover:bg-primary-200 focus:ring-primary-600 focus:ring-2 ml-4 first:ml-0';
  const popOverButtonStyle = 'flex items-center p-2 rounded focus:outline-none focus:ring-primary-600 focus:bg-primary-200 focus:ring-2 disabled:bg-white';
  const handleBookClick = async () => {
    setIsCheckingAvailability(true);
    try {
      const {
        adult_count: adult_count_0,
        childAges: childAges_0
      } = user;
      const pax = paxAges(adult_count_0, childAges_0);
      const {
        mutateAsync: checkTripAvailability
      } = api.trip.checkTripAvailability.useMutation();
      const tripData = await checkTripAvailability({
        tripId: uniqueId,
        pax
      });
      const availabilityResult = tripData?.data;
      if (availabilityResult.result.includes('FAIL_TRANSPORT') || availabilityResult.result === 'ERROR') {
        // Stay on trip page, show error message
        sessionStorage.setItem('journey_availability_result', JSON.stringify(availabilityResult));
        sessionStorage.setItem('booking_disabled', 'true');
        // Trigger page reload to show banner
        window.location.reload();
      } else if (availabilityResult.priceChange && !availabilityResult.priceChange.isPriceHeld) {
        // Go to checkout with price change message
        sessionStorage.setItem('price_change_message', JSON.stringify(availabilityResult));
        if (actions?.book) globalThis.location.href = getPathname({
          href: actions.book,
          locale
        });
      } else if (availabilityResult.result.includes('FAIL_ACCOMMODATION')) {
        // Go to checkout with accommodation message
        sessionStorage.setItem('journey_availability_result', JSON.stringify(availabilityResult));
        if (actions?.book) globalThis.location.href = getPathname({
          href: actions.book,
          locale
        });
      } else if (actions?.book) {
        // All good, proceed to checkout
        globalThis.location.href = getPathname({
          href: actions.book,
          locale
        });
      }
    } catch (_error) {
      sessionStorage.setItem('booking_disabled', 'true');
      setShowErrorToast(true);
      window.location.reload();
    } finally {
      setIsCheckingAvailability(false);
    }
  };
  const handleNotificationClose = () => {
    setShowErrorToast(false);
  };
  const [cityDropdownState, setCityDropdownState] = useState<string | null>(null);

  // Make the API call once at the parent level
  const {
    isLoading,
    data: allOrigins = []
  } = api.cms.origins.useQuery({
    tripId: uniqueId,
    locale
  }, {
    ...TRPC_SETTINGS.QUERY_NO_REFETCH
  });
  const firstLegOriginId = getFirstLegOriginId(items);
  const isUS = isUSBlueprintTrip({
    origins: allOrigins,
    originId: firstLegOriginId,
    items: items as {
      country?: {
        countryId: string;
      } | null;
    }[]
  });

  // Update regions availability whenever origins data changes
  const regionsWithOrigins = useMemo(() => {
    if (isLoading || allOrigins.length === 0) {
      return {
        UK: true,
        EU: true
      };
    }
    const hasUKOrigins = allOrigins.some(origin => isOriginInRegion(origin, 'UK'));
    const hasEUOrigins = allOrigins.some(origin_0 => isOriginInRegion(origin_0, 'EU'));
    return {
      UK: hasUKOrigins,
      EU: hasEUOrigins
    };
  }, [isLoading, allOrigins]);

  // Handle if origin is changed and reverted to valid initial origin
  const handleOriginSelect = (origin_1, setFieldValue, close) => {
    const newOrigin = origin_1.locationName;
    setFieldValue('closest_city', newOrigin);

    // If the selected origin matches the original journey origin and there are errors, clear them
    if (newOrigin === closest_city && journeyEditErrors.length > 0) {
      setJourneyEditErrors([]);
      // Force reset checking availability state to ensure button is enabled
      setIsCheckingAvailability(false);
    }
    close();
  };

  // Reset loading state when errors exist
  useEffect(() => {
    if (journeyEditErrors && journeyEditErrors.length > 0) {
      setIsCheckingAvailability(false);
    }
  }, [journeyEditErrors, setIsCheckingAvailability]);
  useEffect(() => {
    if (isCheckingAvailability) {
      setLiveRegionMessage(t('common.loading'));
    } else {
      setLiveRegionMessage(null);
    }
  }, [isCheckingAvailability]);
  const FilteredOriginList = ({
    startDate,
    filterCountry,
    onSelect
  }) => {
    // Filter origins based on the selected region
    const filteredOrigins = allOrigins.filter(origin_2 => isOriginInRegion(origin_2, filterCountry === 'UK' ? 'UK' : 'EU')).sort((a, b) => {
      // Sort London to the top
      if (a.locationName === 'London' && b.locationName !== 'London') {
        return -1;
      }
      if (b.locationName === 'London' && a.locationName !== 'London') {
        return 1;
      }
      return a.locationName.localeCompare(b.locationName);
    });
    if (!filteredOrigins.length) {
      return <div className="p-4 text-center">
          <Text variant="emphasis-2">{t('journey.no_origins')}</Text>
        </div>;
    }
    return <div className="flex flex-col gap-1" role="menu">
        {filteredOrigins.map(origin_3 => {
        // Check if the origin is blocked
        const isBlocked = startDate && origin_3.blockoutDates?.some(blockout => isWithinInterval(parseDate(startDate), {
          start: parseDate(blockout.startDate),
          end: parseDate(blockout.endDate)
        })) || false;
        const originButton = <button type="button" className={clsx('text-left rounded hover:bg-primary-200 p-2 outline-none w-full border-b border-neutral-200 focus-visible:ring-2 focus-visible:ring-primary-600', isBlocked && 'opacity-50 cursor-not-allowed', origin_3.locationName === 'London' && 'border-b-2 border-neutral-300')} disabled={isBlocked} onClick={() => !isBlocked && onSelect(origin_3)} onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ' ') && !isBlocked) {
            e.preventDefault();
            onSelect(origin_3);
          }
        }} tabIndex={isBlocked ? -1 : 0} role="menuitem" aria-disabled={isBlocked} data-testid={`origin-option-${origin_3?.locationName}`}>
              <div className="flex items-center gap-1">
                <MapPin decorative size="large" />
                <div>
                  <span className="font-figtree text-[14px] font-normal leading-[140%] text-neutral-800">
                    {origin_3.locationName}
                  </span>
                  {isBlocked && <p className="text-xs text-neutral-500">{t('journey.origin_unavailable_date')}</p>}
                </div>
              </div>
            </button>;
        return <div key={origin_3.id}>{originButton}</div>;
      })}
      </div>;
  };
  return <>
      <div role="status" aria-atomic="true" aria-live="polite" className="sr-only">
        {liveRegionMessage}
      </div>
      <div className="hidden py-3 md:block">
        <div>
          {isBlueprint && !isEarliestCheapestDate && isEditable && <p className="mb-2">{t('journey.edit_details')}</p>}
          {isEarliestCheapestDate && <p className="mb-2">
              {t.rich('journey.cheapest_date_found', {
            cheapestDate: chunks => <TooltipTrigger>
                    <Focusable>
                      <span className="underline cursor-pointer">{chunks}</span>
                    </Focusable>
                    <Tooltip placement="bottom">
                      {t('journey.cheapest_date_tooltip', {
                  pax: adult_count + childAges.length,
                  city: closest_city,
                  nights: details.tripInDays - 1,
                  date: formatSlashedDDMMYYYY(futurePricingTiers?.cheapestAvailableDateUpdated ?? futurePricingTiers?.cheapestAvailableDate ?? effectiveStartDate)
                })}
                    </Tooltip>
                  </TooltipTrigger>
          })}
            </p>}
          {journeyEditErrors.length && !journeyEditErrors.includes('UNKNOWN_FAILURE') ? <div className="flex flex-col px-4 my-2 md:px-0 text-status-error prose-p:mt-0 prose-a:text-status-error" data-testid="JourneyTopBar-EditFormErrors">
              {journeyEditErrors.map((errorMessage, i) => <div key={`JourneyTopBar-EditFormError-${i}-${errorMessage}`} className="flex flex-row">
                  {i === 0 && <WarningTriangle ariaLabel={t('aria.error')} size="large" />}
                  <span className="ml-2">{editErrorMessage(errorMessage)}</span>
                </div>)}
            </div> : null}
          <EditForm isPopulated={true} isTemplateTrip={isTemplateTrip} isVirtualBlueprint={source.creationSource === 'FROM_VIRTUAL_BLUEPRINT_TEMPLATE' || source.creationSource === 'FROM_VIRTUAL_BLUEPRINT'}>
            {({
            handleSubmit,
            values,
            dirty,
            errors,
            touched
          }) => {
            return <ul className="flex items-center">
                  <li className={inputStyle}>
                    <Popover className="relative">
                      {({
                    open,
                    close: close_0
                  }) => {
                    return <>
                            <PopoverButton disabled={!isEditable} className={clsx(popOverButtonStyle, open && 'ring-primary-600 bg-primary-200 ring-2', hasFieldErrors(errors as FormikErrors<FormValues>, ['startDate', 'endDate', 'duration']) && 'bg-status-error bg-opacity-20 ring-status-error ring-2')} data-ua-click="edit-dates-popover" data-testid="topBar-edit-dates-popover" onClick={() => trackEvent('trip-page-edit-click-dates', {
                        event_category: 'click',
                        event_label: 'trip-page-edit-click-dates'
                      })}>
                              <Stack space="--by-space-100" variant="horizontal">
                                <Calendar ariaLabel={t('aria.date')} size="large" />
                                <span className="typography-body-2">
                                  {values.startDate ? <>
                                      <span className="hidden lg:inline">{t('journey.start_on')} </span>
                                      <span className="typography-emphasis-2">
                                        {formatSlashedDDMMYYYY(values.startDate)}
                                      </span>
                                    </> : <span className="typography-emphasis-2">{t('journey.start_on')}:</span>}
                                </span>
                                {isEditable && <NavArrowDown size="large" decorative />}
                              </Stack>
                            </PopoverButton>
                            {(open || errors.startDate) && <PopoverPanel static className="absolute left-0 right-auto z-10 w-auto mt-2 bg-white border rounded shadow-lg">
                                <Dates simpleDatePicker={isTemplateTrip || isBlueprint || isPlaygroundTrip || isFastOneWay} durationLimiter={{
                          minDurationDays,
                          maxDurationDays
                        }} />
                                <div className="flex items-center justify-between px-4 pt-2 pb-4">
                                  {isBlueprint && futurePricingTiers?.pricingTiers?.length && <div className="flex gap-x-3 typography-caption-1">
                                      <div className="text-center">
                                        <div>£</div>
                                        <div className="leading-[0]">
                                          <span className="inline-block w-1.5 h-1.5 rounded-full border bg-status-low border-neutral-600"></span>
                                        </div>
                                      </div>
                                      <div className="text-center">
                                        <div>££</div>
                                        <div className="space-x-0.5 leading-[0]">
                                          <span className="inline-block w-1.5 h-1.5 rounded-full border bg-status-mid border-neutral-600"></span>
                                          <span className="inline-block w-1.5 h-1.5 rounded-full border bg-status-mid border-neutral-600"></span>
                                        </div>
                                      </div>
                                      <div className="text-center">
                                        <div>£££</div>
                                        <div className="space-x-0.5 leading-[0]">
                                          <span className="inline-block w-1.5 h-1.5 rounded-full border bg-status-high border-neutral-600"></span>
                                          <span className="inline-block w-1.5 h-1.5 rounded-full border bg-status-high border-neutral-600"></span>
                                          <span className="inline-block w-1.5 h-1.5 rounded-full border bg-status-high border-neutral-600"></span>
                                        </div>
                                      </div>
                                    </div>}
                                  <div className="ml-auto">
                                    <Button type="primary" size="small" data-ua-click="datepicker-check-availability-button" data-testid="datepicker-check-availability-button-topBar" aria-busy={isCheckingAvailability} onClick={() => {
                              close_0();
                              handleSubmit();
                            }} loading={isCheckingAvailability} disabled={!values.startDate}>
                                      {t('journey.check_availability')}
                                    </Button>
                                  </div>
                                </div>
                              </PopoverPanel>}
                          </>;
                  }}
                    </Popover>
                  </li>
                  {!isUS && <li className={inputStyle}>
                      <Popover className="relative">
                        {({
                    open: open_0,
                    close: close_1
                  }) => {
                    return <>
                              <PopoverButton disabled={!isEditable} className={clsx(popOverButtonStyle, open_0 && 'ring-primary-600 bg-primary-200 ring-2', hasFieldErrors(errors as FormikErrors<FormValues>, ['closest_city']) && 'bg-status-error bg-opacity-20 ring-status-error ring-2')} data-ua-click="edit-starting-city-popover" data-testid="topBar-edit-starting-city-popover" onClick={() => trackEvent('trip-page-edit-click-origin', {
                        event_category: 'click',
                        event_label: 'trip-page-edit-click-origin'
                      })}>
                                <MapPin decorative size="large" />
                                <span className={`typography-body-2 text-ellipsis ml-2 ${isEditable ? 'mr-2' : ''}`}>
                                  {values.closest_city ? <>
                                      <span className="hidden lg:inline">{t('journey.start_from')} </span>
                                      <span className="typography-emphasis-2">{values.closest_city}</span>
                                    </> : <span className="typography-emphasis-2">{t('journey.start_from')}</span>}
                                </span>
                                {isEditable && <NavArrowDown size="large" className="ml-2" decorative />}
                              </PopoverButton>
                              <PopoverPanel className="absolute z-10 mt-2 bg-white border rounded shadow-lg w-[295px]">
                                {/* Multi-level dropdown UI */}
                                {!cityDropdownState ? <div className="w-full">
                                    <div className="p-4 pb-2 border-b border-neutral-300">
                                      <p className="select-none typography-body-2 text-neutral-900">
                                        {t('journey.origin_heading')}
                                      </p>
                                    </div>
                                    <ul className="flex flex-col w-full">
                                      <li className="border-b border-neutral-300">
                                        <button type="button" className={`p-4 text-left flex items-center justify-between w-full transition-colors ${regionsWithOrigins.UK ? 'hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600' : 'opacity-50 cursor-not-allowed'}`} data-testid="origin-region-UK" onClick={() => {
                                if (regionsWithOrigins.UK) {
                                  setCityDropdownState('UK');
                                }
                              }} onKeyDown={e_0 => {
                                if (e_0.key === 'Enter' || e_0.key === ' ') {
                                  e_0.preventDefault();
                                  if (regionsWithOrigins.UK) {
                                    setCityDropdownState('UK');
                                  }
                                }
                              }} disabled={!regionsWithOrigins.UK} tabIndex={0} aria-haspopup="true">
                                          <span className="font-normal typography-emphasis-2">
                                            {t('journey.origin_uk')}
                                          </span>
                                          {regionsWithOrigins.UK && <NavArrowRight size="large" decorative />}
                                        </button>
                                      </li>
                                      <li>
                                        <button type="button" className={`p-4 text-left flex items-center justify-between w-full transition-colors ${regionsWithOrigins.EU ? 'hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600' : 'opacity-50 cursor-not-allowed'}`} onClick={() => {
                                if (regionsWithOrigins.EU) {
                                  setCityDropdownState('EU');
                                }
                              }} onKeyDown={e_1 => {
                                if (e_1.key === 'Enter' || e_1.key === ' ') {
                                  e_1.preventDefault();
                                  if (regionsWithOrigins.EU) {
                                    setCityDropdownState('EU');
                                  }
                                }
                              }} disabled={!regionsWithOrigins.EU} tabIndex={0} aria-haspopup="true" data-testid="origin-region-EU">
                                          <span className="font-normal typography-emphasis-2">
                                            {t('journey.origin_europe')}
                                          </span>
                                          {regionsWithOrigins.EU && <NavArrowRight size="large" decorative />}
                                        </button>
                                      </li>
                                    </ul>
                                  </div> : <div className="w-full">
                                    <div className="py-4 px-2 border-b-[3px] border-neutral-300 flex items-center">
                                      <button type="button" className="flex items-center gap-2 p-2 rounded typography-emphasis-2 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600" onClick={() => setCityDropdownState(null)} onKeyDown={e_2 => {
                              if (e_2.key === 'Enter' || e_2.key === ' ') {
                                e_2.preventDefault();
                                setCityDropdownState(null);
                              }
                            }} tabIndex={0} aria-label={t('aria.back')}>
                                        <NavArrowLeft decorative />
                                        {cityDropdownState === 'UK' ? t('journey.origin_uk') : t('journey.origin_europe')}
                                      </button>
                                    </div>
                                    <div className="overflow-y-auto max-h-60" role="menu">
                                      <Field name="closest_city">
                                        {({
                                form: {
                                  setFieldValue: setFieldValue_0
                                }
                              }) => <div>
                                            <FilteredOriginList startDate={values.startDate} filterCountry={cityDropdownState ?? 'UK'} onSelect={origin_4 => {
                                  handleOriginSelect(origin_4, setFieldValue_0, close_1);
                                }} />
                                          </div>}
                                      </Field>
                                    </div>
                                  </div>}
                              </PopoverPanel>
                            </>;
                  }}
                      </Popover>
                    </li>}
                  <li className={inputStyle}>
                    <Popover className="relative">
                      {({
                    open: open_1
                  }) => <>
                          <PopoverButton disabled={!isEditable} className={clsx(popOverButtonStyle, open_1 && 'ring-primary-600 bg-primary-200 ring-2', hasFieldErrors(errors as FormikErrors<FormValues>, ['adult_count', 'room_count', 'child_count', 'childAges']) && 'bg-status-error bg-opacity-20 ring-status-error ring-2')} data-ua-click="edit-passengers-popover" data-testid="topBar-edit-passengers-popover" onClick={() => trackEvent('trip-page-edit-click-pax', {
                      event_category: 'click',
                      event_label: 'trip-page-edit-click-pax'
                    })} aria-label={t('aria.edit_passengers_current', {
                      people: values.adult_count + values.child_count,
                      rooms: values.room_count
                    })} aria-haspopup="dialog">
                            <Stack space="--by-space-100" variant="horizontal">
                              <Stack space="--by-space-100" variant="horizontal">
                                <User ariaLabel={t('aria.people')} size="large" />
                                {!isTemplateTrip || !isEditable || values.adult_count > 0 || touched.adult_count || touched.child_count ? isEditable ? <span className="typography-emphasis-2" data-testid="passenger-count">
                                      <span className="lg:hidden">{values.adult_count + values.child_count}</span>
                                      <span className="hidden lg:inline">
                                        {t('journey.pax_people', {
                                count: values.adult_count + values.child_count
                              })}
                                      </span>
                                    </span> : <span className="typography-emphasis-2">
                                      {t('journey.pax_adults', {
                              count: values.adult_count
                            })}
                                      {values.child_count > 0 && <>
                                          <span className="px-2">·</span>
                                          {t('journey.pax_children', {
                                count: values.child_count
                              })}
                                        </>}
                                    </span> : <span className="typography-emphasis-2">{t('journey.add_people')}</span>}
                              </Stack>
                              <span className="px-1" />
                              <Stack space="--by-space-100" variant="horizontal">
                                <BedDouble ariaLabel={t('aria.rooms')} size="large" />
                                {!isTemplateTrip || !isEditable || values.room_count > 0 || touched.room_count ? <span className="typography-emphasis-2" data-testid="room-count">
                                    <span className="lg:hidden">{values.room_count}</span>
                                    <span className="hidden lg:inline">
                                      {t('journey.pax_rooms', {
                                count: values.room_count
                              })}
                                    </span>
                                  </span> : <span className="typography-emphasis-2">{t('journey.add_rooms')}</span>}
                                {isEditable && <NavArrowDown size="large" decorative />}
                              </Stack>
                            </Stack>
                          </PopoverButton>
                          <PopoverPanel className="absolute z-10 p-4 mt-2 bg-white border rounded shadow-lg w-max" aria-label={t('aria.edit_passengers')} role="dialog" aria-modal="false">
                            <Details />
                          </PopoverPanel>
                        </>}
                    </Popover>
                  </li>
                  {dirty && !isTemplateTrip && <li className="pl-2 xl:pl-4 last:ml-auto">
                      {isTemplateTrip || isBlueprint || isPlaygroundTrip ? <Button type={dirty ? 'primary' : 'secondary'} size="small" data-ua-click="check-availability-button" data-testid="check-availability-button-topBar" aria-busy={isCheckingAvailability} onClick={() => {
                  handleSubmit();
                }} loading={isCheckingAvailability}>
                          {t('journey.check_availability')}
                        </Button> : <TooltipTrigger>
                          <Focusable>
                            <Button type={dirty ? 'primary' : 'secondary'} size="small" data-ua-click="check-availability-button" onClick={() => {
                      handleSubmit();
                    }} loading={isCheckingAvailability}>
                              {t('journey.check_availability')}
                            </Button>
                          </Focusable>
                          <Tooltip>{t('journey.itinerary_change_tooltip')}</Tooltip>
                        </TooltipTrigger>}
                    </li>}
                  <li className="last:ml-auto">
                    {/* TODO: desperate for a refactor, confusing button/s logic */}
                    <div data-testid="topBar-button-wrapper" className="flex items-center space-x-4">
                      {isEnquireOnly && <TooltipTrigger>
                          <Focusable>
                            <div>
                              <Button is="a" type="secondary" size="small" data-ua-click="edit-button" href={actions?.enquireEdit}>
                                <span className="mr-1">
                                  <ChatBubbleLeftRightSolid decorative isInline size="small" />
                                </span>
                                {t('journey.edit')}
                              </Button>
                            </div>
                          </Focusable>
                          <Tooltip>{t('journey.itinerary_change_tooltip')}</Tooltip>
                        </TooltipTrigger>}
                      {!isEnquireOnly && <div className="w-[100px] text-center xl:w-auto xl:text-left">
                          <ContactUsButton />
                        </div>}
                      {isTemplateTrip && <Button onClick={handleSubmit} loading={isCheckingAvailability} data-ua-click="check-availability-button" aria-busy={isCheckingAvailability} disabled={!isEditable}>
                          {t('journey.check_availability')}
                        </Button>}
                      {(!isTemplateTrip || !isBlueprint) && (isEnquireOnly ? <Button is="a" data-ua-click="book-button" href={actions?.enquireBook} loading={isCheckingAvailability} aria-busy={isCheckingAvailability}>
                            <span className="mr-1 pointer-events-none">
                              <ChatBubbleLeftRightSolid decorative isInline size="small" />
                            </span>
                            {bookButtonCopy}
                          </Button> : isStartDateInPast ? <TooltipTrigger>
                            <Focusable>
                              <span>
                                <Button disabled>{bookButtonCopy}</Button>
                              </span>
                            </Focusable>
                            <Tooltip>{t('journey.dates_past')}</Tooltip>
                          </TooltipTrigger> : <BookButton disabled={isBookDisabled} href={actions?.book} loading={isCheckingAvailability} onClick={handleBookClick} aria-busy={isCheckingAvailability}>
                            {bookButtonCopy}
                          </BookButton>)}
                    </div>
                  </li>
                </ul>;
          }}
          </EditForm>
        </div>
      </div>
      {showErrorToast && <Notification state="error" message={t('content.feedback.availability_error')} onClose={handleNotificationClose} />}
    </>;
}